import {CommonModule} from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  effect,
  Signal,
} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {ActivatedRoute, Router} from '@angular/router';
import {ManagedCampaignsStore} from '@em/campaigns/data-access';
import {
  generatePriceCrawlTemplate,
  GOOGLE_AUTOMATED_DISCOUNTS_TEMPLATE,
  ProductGroupsFacade,
} from '@em/data-feed/data-access-products';
import {ProductGroupEntity} from '@em/data-feed/util-types';
import {
  EmIconModule,
  EmInputModule,
  EmSelectModule,
  EmPrimaryButtonComponent,
  EmSpinnerModule,
  EmBoxModule,
} from '@em/shared/ui';
import {
  EmSuggestedPlanComponent,
  SuggestedPlanService,
} from '@em/subscription/feature';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {EmProductGroupsListComponent} from '../product-groups-list/product-groups-list.component';
import {map} from 'rxjs/operators';
import {CountryService} from '@em/user-account/data-access';
import {GroupDialogService} from '../../services/group-dialog/group-dialog.service';

const DEFAULT_TYPE_VALUE = 'all';
const DEFAULT_SORT_VALUE = 'name';
const DEFAULT_SORT_DIRECTION_VALUE = 'asc';

interface ParsedFilters {
  type: string;
  sortBy: string;
  searchQuery: string;
}

@Component({
  selector: 'em-product-groups-page',
  templateUrl: './product-groups-page.component.html',
  styleUrls: ['./product-groups-page.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatMenuModule,
    MatIconModule,
    EmProductGroupsListComponent,
    EmSuggestedPlanComponent,
    EmSelectModule,
    EmIconModule,
    EmInputModule,
    EmPrimaryButtonComponent,
    EmSpinnerModule,
    EmBoxModule,
  ],
  providers: [SuggestedPlanService],
})
export class EmProductGroupsPageComponent implements OnInit {
  readonly #suggestedPlanService = inject(SuggestedPlanService);
  readonly #groupDialogService = inject(GroupDialogService);
  readonly #productGroupsFacade = inject(ProductGroupsFacade);
  readonly #manageCampaigns = inject(ManagedCampaignsStore);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #translateService = inject(TranslateService);
  readonly #router = inject(Router);
  readonly #fb = inject(FormBuilder);
  readonly #countryService = inject(CountryService);

  filterForm!: FormGroup;

  readonly groupsQuery = this.#productGroupsFacade.getGroups(
    this.#countryService.countrySignal,
  );
  readonly productGroups = this.groupsQuery.data;
  readonly isLoadingGroups = this.groupsQuery.isLoading;

  // --- URL State Source of Truth ---
  readonly urlFilters: Signal<ParsedFilters> = toSignal(
    this.#activatedRoute.queryParams.pipe(
      map((params) => ({
        type: params['type'] || DEFAULT_TYPE_VALUE,
        sortBy:
          (params['sortBy'] || DEFAULT_SORT_VALUE) +
          '-' +
          (params['sortDirection'] || DEFAULT_SORT_DIRECTION_VALUE),
        searchQuery: params['searchQuery'] || '',
      })),
    ),
    {
      initialValue: {
        type: DEFAULT_TYPE_VALUE,
        sortBy: `${DEFAULT_SORT_VALUE}-${DEFAULT_SORT_DIRECTION_VALUE}`,
        searchQuery: '',
      },
    },
  );

  // --- Computed States ---
  readonly showSuggestedPlan = toSignal(
    this.#suggestedPlanService.showSuggestedPlan$,
    {initialValue: false},
  );

  readonly hasSpdGroup = computed(() => {
    const groups = this.productGroups();
    if (groups?.length) {
      return groups.some((g: ProductGroupEntity) => g.isGoogleSPD);
    }
    return false;
  });

  // Watches both the product groups AND the URL parameters automatically
  readonly filteredGroups = computed(() => {
    const groups = this.productGroups();
    const filters = this.urlFilters();

    if (!groups) return [];

    return groups
      .filter((g: ProductGroupEntity) => {
        if (filters.searchQuery) {
          return this.#translateService
            .instant(g.name)
            .toLowerCase()
            .includes(filters.searchQuery.toLowerCase());
        }
        return true;
      })
      .filter((g: ProductGroupEntity) => {
        if (filters.type && filters.type !== DEFAULT_TYPE_VALUE) {
          if (filters.type === 'radar') return !!g.isPriceCrawl;
          if (filters.type === 'gad') return !!g.isGoogleSPD;
          if (filters.type === 'custom') {
            return !g.isPriceCrawl && !g.isGoogleSPD;
          }
          return false;
        }
        return true;
      })
      .sort((a: ProductGroupEntity, b: ProductGroupEntity) => {
        if (filters.sortBy.includes('name')) {
          const nameA = this.#translateService.instant(a.name);
          const nameB = this.#translateService.instant(b.name);
          return filters.sortBy.includes('asc')
            ? nameA.localeCompare(nameB) || 0
            : nameB.localeCompare(nameA) || 0;
        }

        if (filters.sortBy.includes('type')) {
          const getTypeWeight = (g: ProductGroupEntity) =>
            g.isGoogleSPD || g.isPriceCrawl
              ? g.isGoogleSPD
                ? g.isPriceCrawl
                  ? 0
                  : 1
                : 2
              : 3;

          const av = getTypeWeight(a);
          const bv = getTypeWeight(b);

          return filters.sortBy.includes('asc') ? av - bv : bv - av;
        }
        return 0;
      });
  });

  constructor() {
    // Keep the Form UI in sync whenever the URL parameters change
    effect(() => {
      const filters = this.urlFilters();
      if (this.filterForm) {
        this.filterForm.patchValue(filters, {emitEvent: false});
      }
    });
  }

  ngOnInit(): void {
    // 1. Initialize the Form with current URL values as initial states
    const initialFilters = this.urlFilters();
    this.filterForm = this.#fb.group({
      type: [initialFilters.type],
      sortBy: [initialFilters.sortBy],
      searchQuery: [initialFilters.searchQuery],
    });

    // 2. Listen to form controls to patch the URL, not the local state
    this.filterForm.valueChanges.subscribe((formValues) => {
      this.updateUrl(formValues);
    });

    // 3. Trigger initial data loads
    this.#manageCampaigns.loadAllCampaigns(true);
  }

  // Pure method to construct state transitions into query strings
  private updateUrl(filters: ParsedFilters): void {
    const queryParams: Record<string, string | undefined> = {};

    queryParams['type'] =
      filters.type !== DEFAULT_TYPE_VALUE ? filters.type : undefined;

    if (
      filters.sortBy.includes(DEFAULT_SORT_VALUE) &&
      filters.sortBy.includes(DEFAULT_SORT_DIRECTION_VALUE)
    ) {
      queryParams['sortBy'] = undefined;
      queryParams['sortDirection'] = undefined;
    } else {
      const [sort, direction] = filters.sortBy.split('-');
      queryParams['sortBy'] = sort;
      queryParams['sortDirection'] = direction;
    }

    queryParams['searchQuery'] = filters.searchQuery || undefined;

    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  addNewGroup(): void {
    this.#groupDialogService
      .openAddGroupAndSave(this.#countryService.countrySignal())
      .subscribe();
  }

  addRadarGroup(existingGroups: ProductGroupEntity[]): void {
    this.#groupDialogService
      .openEditGroupAndSave(
        this.#countryService.countrySignal(),
        generatePriceCrawlTemplate(existingGroups.map((g) => g.name)),
      )
      .subscribe();
  }

  addAutomatedDiscountGroup(): void {
    this.#groupDialogService
      .openEditGroupAndSave(
        this.#countryService.countrySignal(),
        GOOGLE_AUTOMATED_DISCOUNTS_TEMPLATE,
      )
      .subscribe();
  }
}
